/**
 * Load HRTs Actor
 *
 * Loads available HRTs (Health-Related Treatments) for an organization.
 * This is used to populate the storefront with available services.
 *
 * Uses /api/kiosk/hrts instead of Server Actions to avoid RSC streaming issues on Safari.
 */

import type { GetHrtsQuery } from "@frontend/lib/graphql/generated-gql";
import { KioskHrtType } from "@frontend/lib/graphql/generated-gql";
import { fromPromise } from "xstate";
import { z } from "zod";
import { trackNetworkRequest } from "../../lib/network-diagnostics";
import type { ActorWithMetadata } from "./send-sms.actor";

/**
 * Input Schema
 */
const LoadHrtsInputSchema = z.object({
  orgId: z.number(),
});

type LoadHrtsInput = z.infer<typeof LoadHrtsInputSchema>;

/**
 * HRT Type (simplified from GetHrtsQuery response)
 */
export type HrtType = GetHrtsQuery["hrts"][number];

/**
 * Success Schema
 */
const LoadHrtsSuccessSchema = z.object({
  hrts: z.array(z.any()), // Using z.any() for complex HRT type from GraphQL
  hrtIds: z.array(z.number()),
  count: z.number(),
  defaultHrt: z.any().nullable(), // The default kiosk HRT
  epsHrt: z.any().nullable(), // The EPS HRT
  workersCompHrt: z.any().nullable(), // The Workers Comp HRT
  travelInsuranceHrt: z.any().nullable(), // The Travel Insurance HRT
});

type LoadHrtsSuccess = z.infer<typeof LoadHrtsSuccessSchema>;

/**
 * Error Schema
 */
const LoadHrtsErrorSchema = z.object({
  code: z.enum([
    "VALIDATION_ERROR",
    "LOAD_FAILED",
    "NETWORK_ERROR",
    "NO_HRTS_FOUND",
  ]),
  message: z.string(),
  retryable: z.boolean(),
});

type LoadHrtsError = z.infer<typeof LoadHrtsErrorSchema>;

/**
 * Dependencies
 */
export interface LoadHrtsDependencies {
  analytics?: {
    track: (event: string, properties?: Record<string, unknown>) => void;
  };
}

/**
 * Load HRTs Actor Implementation
 */
const loadHrtsActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: LoadHrtsInput & { deps?: LoadHrtsDependencies };
  }): Promise<LoadHrtsSuccess> => {
    const { orgId, deps } = input;

    // Validate input
    const validated = LoadHrtsInputSchema.safeParse({ orgId });

    if (!validated.success) {
      const errorData: LoadHrtsError = {
        code: "VALIDATION_ERROR",
        message: `Validation failed: ${validated.error.message}`,
        retryable: false,
      };
      const error = new Error(errorData.message);
      Object.assign(error, errorData);
      throw error;
    }

    const tracker = trackNetworkRequest("loadHrts", { orgId });

    try {
      const response = await fetch(`/api/kiosk/hrts?orgId=${orgId}`, {
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error(
          `API returned ${response.status}: ${response.statusText}`,
        );
      }

      const data = (await response.json()) as { hrts: GetHrtsQuery["hrts"] };
      const rawHrts = data.hrts;

      tracker.success();

      const hrts = rawHrts
        .filter((hrt) => hrt.isBookableOnStorefront)
        .filter((hrt) => hrt.isConsumerFacing);

      if (!hrts || hrts.length === 0) {
        const errorData: LoadHrtsError = {
          code: "NO_HRTS_FOUND",
          message: `No HRTs found for organization ${orgId}`,
          retryable: false,
        };
        const error = new Error(errorData.message);
        Object.assign(error, errorData);
        throw error;
      }

      const hrtIds = hrts.map((hrt) => hrt.hrtId);

      // Find HRTs by KioskHrtType flags
      const defaultHrt = hrts.find((hrt) =>
        hrt.HrtFlags.some(
          (flag) => flag.kioskHrtType === KioskHrtType.DefaultKiosk,
        ),
      );

      const epsHrt = hrts.find((hrt) =>
        hrt.HrtFlags.some((flag) => flag.kioskHrtType === KioskHrtType.Eps),
      );

      const workersCompHrt = hrts.find((hrt) =>
        hrt.HrtFlags.some(
          (flag) => flag.kioskHrtType === KioskHrtType.WorkersComp,
        ),
      );

      const travelInsuranceHrt = hrts.find((hrt) =>
        hrt.HrtFlags.some(
          (flag) => flag.kioskHrtType === KioskHrtType.TravelInsurance,
        ),
      );

      return {
        hrts,
        hrtIds,
        count: hrts.length,
        defaultHrt: defaultHrt || null,
        epsHrt: epsHrt || null,
        workersCompHrt: workersCompHrt || null,
        travelInsuranceHrt: travelInsuranceHrt || null,
      };
    } catch (error) {
      tracker.error(error);

      // Check if it's already our structured error
      if (error instanceof Error && "code" in error) {
        throw error;
      }

      const errorData: LoadHrtsError = {
        code: "LOAD_FAILED",
        message: error instanceof Error ? error.message : "Failed to load HRTs",
        retryable: true,
      };
      const err = new Error(errorData.message);
      Object.assign(err, errorData);
      throw err;
    }
  },
);

/**
 * Load HRTs Actor with Metadata
 */
export const loadHrtsActor: ActorWithMetadata = {
  impl: loadHrtsActorImpl,
  metadata: {
    displayName: "loadHrts",
    description:
      "Load available HRTs (treatments/services) for an organization",
    category: "data",
    retryable: true,
    timeout: 15000, // 15 seconds
    errorCodes: [
      "VALIDATION_ERROR",
      "LOAD_FAILED",
      "NETWORK_ERROR",
      "NO_HRTS_FOUND",
    ],
    inputSchema: LoadHrtsInputSchema,
    successSchema: LoadHrtsSuccessSchema,
    errorSchema: LoadHrtsErrorSchema,
    onSuccess: (result: unknown) => {
      const success = result as LoadHrtsSuccess;
      return {
        hrts: success.hrts,
        hrtIds: success.hrtIds,
        hrtCount: success.count,
        defaultHrt: success.defaultHrt,
        epsHrt: success.epsHrt,
        workersCompHrt: success.workersCompHrt,
        travelInsuranceHrt: success.travelInsuranceHrt,
        hrtsError: undefined,
      };
    },
    onError: (error: unknown) => {
      const err = error as LoadHrtsError;
      return {
        hrtsError: {
          code: err.code,
          message: err.message,
          retryable: err.retryable,
        },
      };
    },
  },
};
